
import React from "react";
import { z } from "zod";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import TierQuestions from "./TierQuestions";

const tierSpecificSchema = z.object({
  firstProgram: z.enum(["yes", "no"]).optional(),
  exerciseFamiliarity: z.array(z.string()).optional(),
  confidenceLevel: z.string().optional(),
  baselineMetrics: z.string().optional(),
  equipmentPreference: z.enum(["bodyweight_only", "freeweights", "mixture"]).optional(),
});

type BeginnerTierQuestionsProps = {
  onSubmit: (data: z.infer<typeof tierSpecificSchema>) => void;
  onBack: () => void;
  isSaving: boolean;
};

export default function BeginnerTierQuestions({ 
  onSubmit, 
  onBack, 
  isSaving 
}: BeginnerTierQuestionsProps) {
  const defaultValues = {
    firstProgram: "no" as const,
    exerciseFamiliarity: [] as string[],
    confidenceLevel: "3",
    baselineMetrics: "",
    equipmentPreference: "mixture" as const,
  };
  
  const title = "Beginner Program Questions";
  const description = "Establishing a baseline with foundational movements, gradual progression, and injury prevention for those new to structured exercise.";

  return (
    <TierQuestions
      title={title}
      description={description}
      schema={tierSpecificSchema}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onBack={onBack}
      isSaving={isSaving}
    >
      {(form) => (
        <>
          <FormField
            control={form.control}
            name="firstProgram"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/90 text-sm">
                  Is this the client's first structured fitness program?
                </FormLabel>
                <div className="flex gap-3 mt-2">
                  <Button
                    type="button"
                    variant={field.value === "yes" ? "purple" : "outline"}
                    className={field.value === "yes" ? "" : "bg-white/10 text-white hover:bg-white/20 border-white/30"}
                    onClick={() => field.onChange("yes")}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={field.value === "no" ? "purple" : "outline"}
                    className={field.value === "no" ? "" : "bg-white/10 text-white hover:bg-white/20 border-white/30"}
                    onClick={() => field.onChange("no")}
                  >
                    No
                  </Button>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="exerciseFamiliarity"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/90 text-sm">
                  Exercise Familiarity: Which basic exercises are familiar?
                </FormLabel>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                  {["Bodyweight Squat", "Push-Up", "Lunge", "Plank", "None of the above"].map((exercise) => {
                    const isSelected = field.value?.includes(exercise) || false;
                    return (
                      <div
                        key={exercise}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected 
                            ? "border-[#8B5CF6]/70 bg-[#8B5CF6]/20" 
                            : "border-white/20 bg-white/10 hover:bg-white/15"
                        }`}
                        onClick={() => {
                          const updatedValues = isSelected
                            ? (field.value || []).filter(value => value !== exercise)
                            : [...(field.value || []), exercise];
                          field.onChange(updatedValues);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-sm flex items-center justify-center ${
                            isSelected ? "bg-[#8B5CF6]" : "border border-white/50"
                          }`}>
                            {isSelected && <span className="text-white text-xs">✓</span>}
                          </div>
                          <span className="text-white text-sm">{exercise}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="equipmentPreference"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/90 text-sm">
                  Training Equipment: What equipment would you prefer to use for this client?
                </FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                    className="flex flex-col space-y-2 mt-2"
                  >
                    <div className="flex items-center space-x-3 p-3 rounded-lg border border-white/20 bg-white/10 hover:bg-white/15">
                      <RadioGroupItem value="bodyweight_only" id="bodyweight_only" />
                      <label 
                        htmlFor="bodyweight_only" 
                        className="text-white text-sm cursor-pointer flex-1"
                      >
                        Bodyweight Only — No equipment required
                      </label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg border border-white/20 bg-white/10 hover:bg-white/15">
                      <RadioGroupItem value="freeweights" id="freeweights" />
                      <label 
                        htmlFor="freeweights" 
                        className="text-white text-sm cursor-pointer flex-1"
                      >
                        Freeweights — Primarily using weights (dumbbells, barbells)
                      </label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg border border-white/20 bg-white/10 hover:bg-white/15">
                      <RadioGroupItem value="mixture" id="mixture" />
                      <label 
                        htmlFor="mixture" 
                        className="text-white text-sm cursor-pointer flex-1"
                      >
                        Mixture — Combination of bodyweight and weights
                      </label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="baselineMetrics"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/90 text-sm">
                  Baseline Metrics: Current performance metrics (e.g., Max push-ups, plank time, bodyweight squats)
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Enter current performance metrics, such as max consecutive push-ups, max plank hold time in seconds, max consecutive bodyweight squats, etc."
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50 min-h-24 resize-y"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="confidenceLevel"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white/90 text-sm">
                  Confidence Level: How confident are they using gym equipment or doing exercises on their own?
                </FormLabel>
                <FormControl>
                  <div className="mt-4 px-2">
                    <Slider
                      value={[parseInt(field.value || "3")]}
                      min={1}
                      max={5}
                      step={1}
                      showTooltip
                      tooltipContent={(value) => `Level ${value}`}
                      onValueChange={(values) => field.onChange(values[0].toString())}
                      className="my-6"
                    />
                    <div className="flex justify-between mt-2 text-xs text-white/80">
                      <span>Very uncertain</span>
                      <span>Very confident</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      {[1, 2, 3, 4, 5].map((level) => (
                        <div 
                          key={level} 
                          className={`text-center w-8 text-xs ${
                            parseInt(field.value || "3") === level 
                              ? "text-[#8B5CF6] font-medium" 
                              : "text-white/60"
                          }`}
                        >
                          {level}
                        </div>
                      ))}
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </TierQuestions>
  );
}
